import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Flex, Typography } from "antd";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import Modal from "@/common/elements/Modal";
import TabsV2 from "@/common/elements/TabsV2";
import { v4 as uuidV4 } from "uuid";
import { isNonEmptyVariable } from "@/util/common_function";
import useDialog from "@/hooks/useDialog";
import { useDispatch } from "react-redux";
import { setIrisEnrolleId, setIrisSearchId } from "@/slices/bookingsSlice";
import bI2Official from "@/assets/BI2_block_logo_new.jpg";
import dayjs from "dayjs";
import PersonInformation from "@/components/IrisHistory/PersonInformation";
import ArrestDeatils from "@/components/IrisHistory/ArrestDeatils";
import ReleaseDetails from "@/components/IrisHistory/ReleaseDetails";
import Sid from "@/components/IrisHistory/Sid";
import Flags from "@/components/IrisHistory/Flags";
import Aliases from "@/components/IrisHistory/Aliases";
import Charges from "@/components/IrisHistory/Charges";
import Smt from "@/components/IrisHistory/Smt";

const { Text } = Typography;

// Sentinel "not released" date the API sends for still-in-custody records.
// Only used to blank the Released Date column in the table below — no
// longer used to gate the Continue/close flow (see confirmAndReturn).
const UNRELEASED_DATE = "1900-01-01T00:00:00";

const SearchIrisDetailsPopup = ({
  irisSearchDetailsPopupOpen,
  irisSearchData,
  setisSearchIrisDetailsPopupOpen,
  returnData,
  header = "National Iris Match",
  // readOnly: pure "view details" usage (e.g. Bookings' "Iris History" button)
  // where there's nothing to return/apply — Continue just closes the popup
  // instead of running the folder-search confirmation flow.
  readOnly = false,
}) => {
  const { showDialog } = useDialog();
  const dispatch = useDispatch();

  const [selectedRecord, setSelectedRecord] = useState(
    () => irisSearchData?.enrolledIrisList?.[0] ?? null
  );

  const [photoIndex, setPhotoIndex] = useState(0);
  const tableRef = useRef(null);

  // photos come back as a separate list from enrolledIrisList, so tie them to
  // the selected row by calendar day: a photo belongs to the row if it was
  // taken on the row's booking date or its IRIS release date.
  const photos = useMemo(() => {
    const allPhotos = irisSearchData?.photos ?? [];
    const rowDays = [selectedRecord?.dateBooked, selectedRecord?.irisReleasedDate]
      .filter((date) => isNonEmptyVariable(date) && date !== UNRELEASED_DATE)
      .map((date) => dayjs(date))
      .filter((date) => date.isValid())
      .map((date) => date.format("YYYY-MM-DD"));

    if (!rowDays.length) return [];

    return allPhotos.filter(
      (photo) =>
        isNonEmptyVariable(photo?.photoDateTime) &&
        rowDays.includes(dayjs(photo.photoDateTime).format("YYYY-MM-DD"))
    );
  }, [irisSearchData, selectedRecord]);
  const selectedPhoto = photos[photoIndex] ?? null;

  const handlePrevPhoto = () => {
    if (photoIndex > 0) setPhotoIndex(photoIndex - 1);
  };

  const handleNextPhoto = () => {
    if (photoIndex < photos.length - 1) setPhotoIndex(photoIndex + 1);
  };

  // ── table ────────────────────────────────────────────────────────────────

  const tableData = useMemo(() => {
    const rows = (irisSearchData?.enrolledIrisList ?? []).map((item) => ({
      ...item,
      uuid: uuidV4(),
      type: isNonEmptyVariable(item?.bookingNumber) ? "B" : "S",
      displayName: `${item?.lastName ?? ""}, ${item?.firstName ?? ""}`.trim(),
      dateReleasedDisplay:
        item?.dateReleased === UNRELEASED_DATE ? "" : item?.dateReleased,
    }));
    return {
      total: rows.length,
      headers: [
        { key: "type",          text: "Type"         },
        { key: "displayName",   text: "Name"         },
        { key: "oriId",         text: "ORI"          },
        { key: "bookingNumber", text: "Booking #"    },
        { key: "inmateId",      text: "Inmate ID"    },
        { key: "dateBooked",         text: "Booking Date" },
        { key: "dateReleasedDisplay", text: "Released Date"},
      ],
      rows,
    };
  }, [irisSearchData]);

  // Highlight the first row on load so the grid matches the details/photo
  // shown for it. DataTable only highlights on click (and clears its own
  // selection when data changes), so seed it through the ref afterwards.
  useEffect(() => {
    const firstRow = tableData.rows[0];
    if (!firstRow) return;
    tableRef.current?.selectAll([firstRow]);
    setSelectedRecord(firstRow);
    setPhotoIndex(0);
  }, [tableData]);

  // ── tabs ─────────────────────────────────────────────────────────────────

  const sectionItems = useMemo(
    () => [
      {
        id: "1",
        label: "Arrest Details",
        key: "arrest-details",
        children: <ArrestDeatils data={selectedRecord} />,
      },
      {
        id: "2",
        label: "Release Details",
        key: "release-details",
        children: <ReleaseDetails data={selectedRecord} />,
      },
      {
        id: "3",
        label: "SID",
        key: "sid",
        children: <Sid data={selectedRecord} />,
      },
      {
        id: "4",
        label: "Flags",
        key: "flags",
        children: <Flags data={selectedRecord} />,
      },
      {
        id: "5",
        label: "Aliases",
        key: "aliases",
        children: <Aliases data={selectedRecord} />,
      },
      {
        id: "6",
        label: "Charges",
        key: "charges",
        children: <Charges data={selectedRecord} />,
      },
      {
        id: "7",
        label: "SMT",
        key: "smt",
        children: <Smt data={selectedRecord} />,
      },
    ],
    [selectedRecord]
  );

  // ── handlers ─────────────────────────────────────────────────────────────

  const handleRowClick = (rowData) => {
    if (isNonEmptyVariable(rowData)) {
      setSelectedRecord(rowData);
      setPhotoIndex(0);
    }
  };

  const getConfirmMessage = (data) => (
    <>
      <p>The IRIS Search returned a match (EnrolleeID={data?.enrolleeId})</p>
      <div><span>First Name: </span><span>{data?.firstName}</span></div>
      <div><span>Last Name: </span><span>{data?.lastName}</span></div>
      <div><span>Race: </span><span>{data?.race}</span></div>
      <div><span>Gender: </span><span>{data?.gender}</span></div>
      <p>Do you want to use these values to search for a folder in the local system?</p>
    </>
  );

  const handleHide = () => {
    dispatch(setIrisSearchId(0));
    dispatch(setIrisEnrolleId(0));
    setisSearchIrisDetailsPopupOpen(false);
  };

  // Fires from both the Continue button and the modal's close ("X") icon.
  // No more in-jail/dateReleased gating here — any selected record (falling
  // back to the first row if none was explicitly clicked) goes straight to
  // the single-record confirm dialog; "No" just closes like handleHide.
  const confirmAndReturn = () => {
    const record = selectedRecord ?? irisSearchData?.enrolledIrisList?.[0] ?? null;

    if (!isNonEmptyVariable(record)) {
      // Nothing in the list to confirm — nothing to do but close.
      handleHide();
      return;
    }

    showDialog("confirm", {
      title: "",
      content: getConfirmMessage(record),
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        returnData(record, irisSearchData?.isLocalDataMatched);
        setisSearchIrisDetailsPopupOpen(false);
      },
      onCancel: () => {
        dispatch(setIrisSearchId(0));
        dispatch(setIrisEnrolleId(0));
        setisSearchIrisDetailsPopupOpen(false);
      },
    });
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Modal
      show={irisSearchDetailsPopupOpen}
      width="80%"
      header={header}
      onHide={readOnly ? handleHide : confirmAndReturn}
      handleCancel={handleHide}
      footerActionsNeed={false}
      maskClosable={false}
    >
      {/* ── Enrolled iris list table ── */}
      <DataTable
        ref={tableRef}
        width="100%"
        className="table-striped"
        selectionColumnVisible={false}
        totalRecords={tableData.total}
        headers={tableData.headers}
        data={tableData.rows}
        rowUniqueId="uuid"
        onRowClick={handleRowClick}
        noDataMessage="No iris records were found."
      />

      {/* ── B / S legend + photo date ── */}
      <StyledGridRow style={{ marginTop: "1em", marginBottom: "1em" }}>
        <StyledGridCol span={12}>
          <Flex gap="middle" align="start">
            <Flex gap="middle" align="center">
              <Avatar shape="square" size="small">B</Avatar>
              <Text>Bookings</Text>
            </Flex>
            <Flex gap="middle" align="center">
              <Avatar shape="square" size="small" style={{ backgroundColor: "red" }}>
                S
              </Avatar>
              <Text>Sex Offender Registration</Text>
            </Flex>
          </Flex>
        </StyledGridCol>
        <StyledGridCol span={12}>
          <Flex gap="middle" align="center" justify="flex-end">
            <Text>Picture Date:</Text>
            <Text>
              {isNonEmptyVariable(selectedPhoto?.photoDateTime)
                ? dayjs(selectedPhoto.photoDateTime).format("MM/DD/YYYY HH:mm")
                : "-"}
            </Text>
          </Flex>
        </StyledGridCol>
      </StyledGridRow>

      {/* ── Person information form ── */}
      <PersonInformation
        data={selectedRecord}
        photos={photos}
        selectedPhoto={selectedPhoto}
        photoIndex={photoIndex}
        onPrevPhoto={handlePrevPhoto}
        onNextPhoto={handleNextPhoto}
      />

      {/* ── Detail tabs ── */}
      <TabsV2 items={sectionItems} showExtra={false} />

      {/* ── Footer: BI2 branding + IDs + close ── */}
      <hr style={{ border: "1px solid #d9d9d9", margin: "16px 0" }} />
      <StyledGridRow gutter={16} style={{ marginBottom: "1em" }} align="middle">
        <StyledGridCol span={4}>
          <Flex gap="middle" align="center">
            <img alt="BI2 Logo" style={{ width: "3em" }} src={bI2Official} />
            <Flex vertical align="flex-start">
              <Text strong>Powered By</Text>
              <Text strong>BI2 Technologies</Text>
            </Flex>
          </Flex>
        </StyledGridCol>

        <StyledGridCol span={5}>
          <Flex gap="small" align="center">
            <Text>Enrollee ID:</Text>
            <Text strong>{selectedRecord?.enrolleeId || "-"}</Text>
          </Flex>
        </StyledGridCol>

        <StyledGridCol span={5}>
          <Flex gap="small" align="center">
            <Text>Merged IDs:</Text>
            <Text>{selectedRecord?.mergedId || "-"}</Text>
          </Flex>
        </StyledGridCol>

        <StyledGridCol span={4}>
          <Flex gap="small" align="center">
            <Text>Transaction ID:</Text>
            <Text strong>{selectedRecord?.transactionId || "N/A"}</Text>
          </Flex>
        </StyledGridCol>

        <StyledGridCol span={6}>
          <Flex justify="flex-end">
            <Button
              color="primary"
              variant="solid"
              onClick={readOnly ? handleHide : confirmAndReturn}
            >
              {readOnly ? "Close" : "Continue"}
            </Button>
          </Flex>
        </StyledGridCol>
      </StyledGridRow>
    </Modal>
  );
};

export default SearchIrisDetailsPopup;
